import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "E-Lite Package",
  "Basic Package",
  "Premium Package",
  "Not sure yet",
];

export default function CTASection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share at least your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "landing-cta" });
      toast.success("Thanks! Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid="section-cta"
      className="pt-14 sm:pt-20 pb-6 sm:pb-8 relative"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] overflow-hidden relative hairline grain">
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-12 p-10 sm:p-14 bg-slate-50 dark:bg-[#0F172A]/40">
              <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400 font-semibold">
                Enquire Now
              </div>
              <h3 className="font-display mt-2 text-2xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
                Book A Free Course Consultation
              </h3>
              <form className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5" onSubmit={submit}>
                <input
                  data-testid="lead-input-name"
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  className="sm:col-span-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50"
                />
                <input
                  data-testid="lead-input-email"
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={update("email")}
                  required
                  className="sm:col-span-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50"
                />
                <input
                  data-testid="lead-input-phone"
                  type="tel"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={update("phone")}
                  required
                  minLength={6}
                  className="sm:col-span-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50"
                />
                <select
                  data-testid="lead-input-interest"
                  value={form.interest}
                  onChange={update("interest")}
                  className="sm:col-span-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50"
                >
                  <option value="">I'm interested in…</option>
                  {INTERESTS.map((it) => (
                    <option key={it} value={it}>
                      {it}
                    </option>
                  ))}
                </select>
                <textarea
                  data-testid="lead-input-message"
                  placeholder="Tell us a bit about your goals (optional)"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  className="sm:col-span-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50 resize-none"
                />
                <button
                  data-testid="lead-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="sm:col-span-2 inline-flex justify-center items-center gap-2 rounded-xl bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3.5 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Book My Free Consultation <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
                <p className="sm:col-span-2 text-[11px] text-slate-500 dark:text-slate-400">
                  By submitting, you agree to be contacted about SkillPreneur Academy programs and accept our{" "}
                  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" data-testid="lead-link-terms" className="text-[#2563EB] dark:text-[#06B6D4] font-medium underline-offset-2 hover:underline">Terms &amp; Conditions</a>
                  ,{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="lead-link-privacy" className="text-[#2563EB] dark:text-[#06B6D4] font-medium underline-offset-2 hover:underline">Privacy Policy</a>
                  {" "}and{" "}
                  <a href="/refund-policy" target="_blank" rel="noopener noreferrer" data-testid="lead-link-refund" className="text-[#2563EB] dark:text-[#06B6D4] font-medium underline-offset-2 hover:underline">Refund &amp; Cancellation Policy</a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
